import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Headphones, Info, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import ComingSoon from "../components/app/ComingSoon";

/**
 * CRM is an optional module — activates only when the workspace has customer
 * identity data (real emails, phones, or names). When the only customers are
 * anonymous marketplace buyers, show an explanation rather than an unusable UI.
 */
export default function CRM() {
  const [identity, setIdentity] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/intelligence/customer-identity");
        setIdentity(data);
      } catch {}
    })();
  }, []);

  if (identity && !identity.available) {
    return (
      <div className="max-w-3xl mx-auto py-12" data-testid="crm-no-identity">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">CRM</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
          CRM activates when customer identity is available
        </h1>
        <div className="mt-8 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 flex gap-4">
          <div className="h-12 w-12 rounded-xl bg-orange-600/10 border border-orange-600/20 flex items-center justify-center flex-shrink-0">
            <Headphones className="h-5 w-5 text-orange-500" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-zinc-300 leading-relaxed">
              The platforms you've imported from don't expose customer identity (email, phone, or real names).
              That's normal for marketplaces like Meesho, Amazon, and Flipkart. Milkath has still created
              {identity.total_customers > 0 ? ` ${identity.total_customers} anonymous customer records ` : " customer records "}
              so revenue analytics keep working — but CRM features (segments, lifecycle campaigns, messaging)
              need identity to be meaningful.
            </p>
            <p className="mt-3 text-sm text-zinc-400">
              CRM will turn on automatically as soon as any imported customer has an email or phone number.
            </p>
            <Link to="/imports" className="inline-flex items-center gap-1 mt-4 text-sm text-orange-500 hover:text-orange-400">
              Connect a source with identity data <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <ComingSoon testid="cs-crm" title="CRM" description="Lightweight CRM built around the unified Milkath customer record. Segments, lifecycle automations, and direct messaging — coming soon." />;
}
